import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import {Button, Stack} from "@mui/material";
import {SxProps, Theme} from "@mui/material/styles";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {surveyMarkdownToPlainText} from "../../shared/surveyMarkdownText.util";
import {RunnerQuestionProps} from "./runnerQuestion.types";
import {surveyControls} from "../surveyRunnerColors";

const ANSWER_WIDTH = 220;

const checkedButtonSx = (selected: boolean): SxProps<Theme> => ({
    width: ANSWER_WIDTH,
    maxWidth: '100%',
    minHeight: 48,
    px: 3,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    '&.Mui-focusVisible': {
        outline: (theme: Theme) => `2px solid ${surveyControls(theme).focus}`,
        outlineOffset: 2,
    },
    transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
    '& .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: selected ? '8px' : 0,
        width: selected ? 20 : 0,
        opacity: selected ? 1 : 0,
        overflow: 'hidden',
        transform: selected ? 'scale(1)' : 'scale(0.7)',
        transition: 'width 150ms ease, opacity 150ms ease, margin-right 150ms ease, transform 150ms ease',
    },
});

function YesNoQuestion({question, localize, language, value, onAnswerChange}: RunnerQuestionProps) {
    const {t} = useTranslation(undefined, {lng: language});

    const [localAnswer, setLocalAnswer] = useState<boolean | undefined>(undefined);

    const selected = value?.booleanValue ?? localAnswer;

    const pick = (next: boolean) => {
        setLocalAnswer(next);
        onAnswerChange?.({...value, booleanValue: next});
    };

    const choices: {value: boolean, label: string}[] = [
        {value: true, label: localize(question.yesLabel) || t('Yes')},
        {value: false, label: localize(question.noLabel) || t('No')},
    ];

    return (
        <Stack
            direction={'row'}
            spacing={2}
            useFlexGap
            role={'radiogroup'}
            aria-label={surveyMarkdownToPlainText(localize(question.title)) || undefined}
            sx={{justifyContent: 'center', flexWrap: 'wrap'}}
        >
            {choices.map(choice => {
                const active = selected === choice.value;

                return (
                    <Button
                        key={String(choice.value)}
                        variant={active ? 'contained' : 'outlined'}
                        disableElevation
                        role={'radio'}
                        aria-checked={active}
                        onClick={() => pick(choice.value)}
                        startIcon={<CheckRoundedIcon aria-hidden sx={{fontSize: 20}}/>}
                        sx={checkedButtonSx(active)}
                    >
                        {choice.label}
                    </Button>
                );
            })}
        </Stack>
    );
}

export default YesNoQuestion;
